import axios from 'axios'
import cloneDeep from 'lodash.clonedeep'
import { i18n } from '../../../plugins/i18n'
import {
  reviewDataTypes,
  crossAnalysisTypes,
  displayAllPrefix,
  makeAggregateOfAll,
  periods,
  generateGraphId
} from '../../../modules/analysis'

export const companiesAnalysisReviews = {
  namespaced: true,
  state: {
    rawAggs: [],                                // API で取得したテーブルデータ
    plots: {},                                  // API で取得したグラフデータ
    aggsLoading: false,                         // テーブルデータ取得中かどうか
    plotsLoading: false,                        // グラフデータ取得中かどうか
    dataType: reviewDataTypes[0],               // データ種別
    date: periods[1],                           // 日, 週, 月
    displayGraphs: [],                          // グラフ表示対象
    graphType: 'bar',                           // グラフ種別
    graphKey: [],
    pageType: crossAnalysisTypes[0],            // 分析軸
    crossSearchField: null,                     // クロス分析軸
    individualAnalysisField: null,              // テーブル表示リスト（= ドリルダウン対象のテーブルの行のfield）
    competitorMode: false,                      // 競合の表示有無
  },
  getters: {
    /**
     * aggs API に渡すパラメータ
     * @param { Object } state
     * @returns { Object }
     */
    aggsParams: state => {
      return {
        analysis_axis: state.pageType.fields,
        cross_analysis_axis: state.crossSearchField?.fields,
        display_competitor: state.competitorMode,
      }
    },
    /**
     * plots API に渡すパラメータ
     * @param { Object } state
     * @returns { Object }
     */
    plotsParams: state => data => {
      return {
        analysis_axis: data.fields[0].key,
        analysis_axis_id: data.fields[0].id,
        cross_analysis_axis: data.fields[1]?.key,
        cross_analysis_axis_id: data.fields[1]?.id,
        period: state.date.id,  // ex. 'week'
        get_competitors_data: !!data.parent_analysis_axis,
        parent_analysis_axis: data.parent_analysis_axis,
        parent_analysis_axis_id: data.parent_analysis_axis_id,
        parent_cross_analysis_axis: data.parent_cross_analysis_axis,
        parent_cross_analysis_axis_id: data.parent_cross_analysis_axis_id,
      }
    },
    /**
     * export API に渡すパラメータ
     * @param { Object } state
     * @param { Boolean } value
     * @return { Void }
     */
    exportParams: state => {
      return {
        analysis_axis: state.pageType.fields,
        cross_analysis_axis: state.crossSearchField?.fields,
        display_competitor: state.competitorMode,
        period: state.date.id, // ex. 'week'
      }
    },
    /**
     * クロス分析の項目一覧
     * @param { Object } state
     * @returns { Array }
     */
    crossSearchFields(state) {
      const arr = [state.pageType]
      if (state.crossSearchField) {
        arr.push(state.crossSearchField)
      }
      return arr
    },
    /**
     * データ種別の項目一覧
     * @returns { Array }
     */
    dataTypes: () => reviewDataTypes,
    /**
     * テーブル レコードID
     * @param { Object } state
     * @returns { String }
     */
    individualAnalysisId(state) {
      return state.individualAnalysisField ? state.individualAnalysisField.id : null
    },
    /**
     * テーブル レコード名
     * @param { Object } state
     * @returns { String }
     */
    individualAnalysisName(state) {
      return state.individualAnalysisField ? state.individualAnalysisField.name : ''
    },
    /**
     * 選択中のレコード
     * @param { Object } state
     * @returns { Array }
     */
    selectedAggIds: state => {
      return state.displayGraphs
    },
    /**
     * テーブルデータ
     * @param { Object } state
     * @param { Object } getters
     * @param rootState
     * @returns { Array }
     */
    aggs(state, getters) {
      if (state.rawAggs.length === 0) return state.rawAggs

      let aggs = cloneDeep(state.rawAggs)
      if(getters.individualAnalysisId) {
        // 個別分析idでaggsを絞り込み
        aggs = aggs.filter(agg => {
          const currentPageTypeField = agg.fields.find(field => field.key === state.pageType.fields)
          return currentPageTypeField.id === getters.individualAnalysisId
        })
      }

      // テーブルの「全」系のfieldsを作成
      const fields = aggs[0].fields.map(field => {
        return {
          id: 0,
          key: field.key,
          name: i18n.t('models.analysis.crossAnalysisTypes.allName')
        }
      })

      if(getters.individualAnalysisId) {
        // テーブル「表示リスト全て」の列の値を変更
        fields[0].name = i18n.t('models.analysis.displayAll')
        fields[0].id = getters.individualAnalysisId
        fields[0].key = displayAllPrefix + fields[0].key
      }

      const agg = {
        ...makeAggregateOfAll(aggs),
        fields: fields,
      }

      if (state.competitorMode) {
        const competitorData = aggs.map(competitorAgg => competitorAgg.competitorData).flat()

        const competitorAgg = makeAggregateOfAll(competitorData)
        const competitorFields = cloneDeep(agg.fields)
        if (state.pageType.id === 'store') {
          competitorFields[0].key = 'competitor_id'
        } else if(state.pageType.id === 'brand') {
          competitorFields[0].key = 'competitor_brand_id'
        }
        if (state.crossSearchField?.id === 'store') {
          competitorFields[1].key = 'competitor_id'
        } else if(state.crossSearchField?.id === 'brand') {
          competitorFields[1].key = 'competitor_brand_id'
        }
        if (getters.individualAnalysisId) {
          competitorFields[0].key = displayAllPrefix + competitorFields[0].key
        }

        agg.competitorData = [
          {
            ...competitorAgg,
            fields: competitorFields,
            parent_analysis_axis: agg.fields[0].key,
            parent_analysis_axis_id: agg.fields[0].id,
            parent_cross_analysis_axis: agg.fields[1]?.key,
            parent_cross_analysis_axis_id: agg.fields[1]?.id,
          }
        ]
      }
      aggs.unshift(agg)
      return aggs
    }
  },
  mutations: {
    /**
     * データ種別の変更
     * @param { Object } state
     * @param { String } dataType
     * @return { Void }
     */
    setDataType(state, dataType) {
      state.dataType = dataType
      if (state.dataType.id === 'totalCounts'){
        state.graphType = 'line'
      } else {
        state.graphType = 'bar'
      }
    },
    /**
     * 日, 週, 月の変更
     * @param { Object } state
     * @param { String } date
     * @return { Void }
     */
    setDate(state, date) {
      state.plots = {}
      state.date = date
    },
    /**
     * グラフ表示対象の変更
     * @param { Object } state
     * @param { String } graphs
     * @return { Void }
     */
    setDisplayGraphs(state, graphs) {
      state.displayGraphs = graphs
    },
    /**
     * 分析軸の変更
     * @param { Object } state
     * @param { Object } axisObj
     * @return { Void }
     */
    setPageType(state, axisObj) {
      state.pageType = axisObj
    },
    /**
     * クロス分析の変更
     * @param { Object } state
     * @param { String } field
     * @return { Void }
     */
    setCrossSearchField(state, field) {
      state.crossSearchField = field
    },
    /**
     * テーブル表示リストの変更
     * @param { Object } state
     * @param { String } field
     * @return { Void }
     */
    setIndividualAnalysisField(state, field) {
      state.individualAnalysisField = field
    },
    /**
     * 競合の表示有無を変更
     * @param { Object } state
     * @param { Boolean } value
     * @return { Void }
     */
    setCompetitorMode(state, value) {
      state.competitorMode = value
    },
    /**
     * テーブルデータの取得
     * @param { Object } state
     * @return { Void }
     */
    readAggs(state) {
      state.aggsLoading = true
      state.plots = {}
      state.rawAggs = []
    },
    /**
     * テーブルデータの取得成功
     * @param { Object } state
     * @return { Void }
     */
    readAggsSuccess(state, data) {
      state.rawAggs = data.map(v => {
        v = v.attributes
        return {
          // ここの key は reviewDataTypes の id と合わせること
          counts: v.posted_count,
          counts_without_rating_null: v.posted_count_without_rating_null,
          totalCounts: v.total_posted_count,
          scores: v.average_score,
          fields: v.fields,
          totalScores: v.total_score,
          storeNames: v.store_names,
          competitorData: v.competitor_data.map((competitorValue) => {
            const attribute = competitorValue.attributes
            return {
              ...attribute,
              // ここの key は reviewDataTypes の id と合わせること
              counts: attribute.posted_count,
              counts_without_rating_null: attribute.posted_count_without_rating_null,
              totalCounts: attribute.total_posted_count,
              scores: attribute.average_score,
              fields: attribute.fields,
              totalScores: attribute.total_score,
              competitorNames: attribute.competitor_names,
            }
          }),
        }
      })
      state.aggsLoading = false
    },
    /**
     * テーブルデータの取得失敗
     * @param { Object } state
     * @return { Void }
     */
    readAggsFailure(state) {
      state.aggsLoading = false
    },
    /**
     * グラフデータの取得
     * @param { Object } state
     * @return { Void }
     */
    readPlots(state) {
      state.plotsLoading = true
    },
    /**
     * グラフデータの取得成功
     * @param { Object } state
     * @param { Array } payload
     * @return { Void }
     */
    readPlotsSuccess(state, responses) {
      // ディープコピー
      const plots = cloneDeep(state.plots)
      responses.forEach(res => {
        const data = res.data.data.attributes
        const params = res.config.params
        state.graphKey = Object.keys(data[state.dataType.dataType])
        if (params.isRequestingDisplayAllPlot) {
          params.analysis_axis = displayAllPrefix + params.analysis_axis
          data.label = i18n.t('models.analysis.displayAll')
          if (params.parent_analysis_axis) {
            data.label += '(競合)'
          }
        }
        let id = params.parent_analysis_axis ? 'competitor-' : ''
        id += params.analysis_axis + '-' + params.analysis_axis_id
        if (params.cross_analysis_axis) {
          id += '|' + params.cross_analysis_axis + '-' + params.cross_analysis_axis_id
        }
        if (!Object.prototype.hasOwnProperty.call(plots, id)) {
          plots[id] = { ...data }
        }
      })
      state.plots = plots // objectは代入し直さないとリアクティブにならない
      state.plotsLoading = false
    },
    /**
     * グラフデータの取得失敗
     * @param { Object } state
     * @return { Void }
     */
    readPlotsFailure(state) {
      state.plotsLoading = false
    },
    /**
     * CSV データの取得
     * @param { Object } state
     * @return { Void }
     */
    exportCsv(state) {
      state.loadError = false
    },
    /**
     * CSV データの取得成功
     * @param { Object } state
     * @return { Void }
     */
    exportCsvSuccess(state) {
      state.loadError = false
    },
    /**
     * CSV データの取得失敗
     * @param { Object } state
     * @return { Void }
     */
    exportCsvFailure(state) {
      state.loadError = true
    }
  },
  actions: {
    /**
     * データ種別の変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setDataType({ commit }, payload) {
      commit('setDataType', payload)
    },
    /**
     * 日, 週, 月の変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setDate({ commit }, payload) {
      commit('setDate', payload)
    },
    /**
     * グラフ表示対象の変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setDisplayGraphs({ commit }, payload) {
      commit('setDisplayGraphs', payload)
    },
    /**
     * 分析軸の変更
     * @param { Function } commit
     * @param rootState
     * @param { String } queryElement
     * @return { Void }
     */
    setPageType({ commit, state, rootState }, queryElement) {
      commit('setPageType', rootState.companiesAnalysisAxes.data.find(v => v.fields === queryElement))
      if (queryElement === state.crossSearchField?.fields) {
        commit('setCrossSearchField', null)
      }
    },
    /**
     * クロス分析の変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setCrossSearchField({ commit }, payload) {
      commit('setCrossSearchField', payload)
    },
    /**
     * テーブル表示リストの変更
     * @param { Function } commit
     * @param { Object } getters
     * @param { String } payload
     * @return { Void }
     */
    setIndividualAnalysisField({ commit, getters }, payload) {
      commit('setIndividualAnalysisField', payload)
      commit('setDisplayGraphs', [generateGraphId(getters.aggs[0])])
    },
    /**
     * 競合の表示有無を変更
     * @param { Function } commit
     * @param { String } payload
     * @return { Void }
     */
    setCompetitorMode({ commit }, payload) {
      commit('setCompetitorMode', payload)
    },
    /**
     * テーブルデータ取得
     * @param { Function } commit
     * @param { Object } getters
     * @param { Object } payload
     * @return { Promise }
     */
    readAggs({ commit, getters, state }, payload) {
      const { companyUuid, params } = payload
      commit('readAggs')
      return axios
        .get(`/api/companies/${ companyUuid }/analysis/reviews/aggregates.json`, {
          headers: {
            'Content-Type': 'application/json'
          },
          params: { ...getters.aggsParams, ...params }
        })
        .then(res => {
          commit('readAggsSuccess', res.data.data)
          if (state.displayGraphs.length === 0) {
            // 「競合データを表示する」にチェックが入っている場合は「全て（競合）」にもチェックが入っている状態にする
            if (getters.aggsParams.display_competitor) {
              commit('setDisplayGraphs', [generateGraphId(getters.aggs[0]), generateGraphId(getters.aggs[0].competitorData[0])])
            } else {
              commit('setDisplayGraphs', [generateGraphId(getters.aggs[0])])
            }
            return
          }
          // ソートを競合データも含めて一遍にやるために一つの配列にまとめる
          let nextDisplayGraphs = [
            ...getters.aggs,
            ...getters.aggs
              .map(v => v.competitorData && v.competitorData[0])
              .filter(v => v)
          ]
            .filter(agg => getters.selectedAggIds.includes(generateGraphId(agg)))
            // 現在チェックされている店舗の競合データも表示する
            .concat([...getters.aggs
              .filter(v => getters.selectedAggIds.includes(generateGraphId(v)))
              .map(v => v.competitorData && v.competitorData[0])
              .filter(v => v)
            ])
            .map(agg => generateGraphId(agg))

          if (nextDisplayGraphs.length === 0) {
            nextDisplayGraphs = [generateGraphId(getters.aggs[0])]
          }
          commit('setDisplayGraphs', nextDisplayGraphs)
        })
        .catch(err => {
          commit('readAggsFailure')
          console.error('error', err)
        })
    },
    /**
     * グラフデータ取得
     * @param { Function } commit
     * @param { Object } getters
     * @param { Object } payload
     * @return { Promise }
     */
    readPlots({ commit, state, getters }, payload) {
      const { companyUuid, params } = payload
      commit('readPlots')
      // まだ取得していないもののみ取得しに行く
      let graphIdList = getters.selectedAggIds.filter(id => {
        return !Object.prototype.hasOwnProperty.call(state.plots, id)
      })
      let filteredDisplayGraphs = []
      getters.aggs.forEach((aggsItem) => {
        graphIdList.forEach((graphId) => {
          if(graphId === generateGraphId(aggsItem)) {
            filteredDisplayGraphs.push(aggsItem)
          } else if(aggsItem.competitorData?.map(competitorId => generateGraphId(competitorId)).includes(graphId)) {
            aggsItem.competitorData.forEach(competitorItem => {
              filteredDisplayGraphs.push(competitorItem)
            })
          }
        })
      })
      if (state.individualAnalysisField) {
        filteredDisplayGraphs = filteredDisplayGraphs.map(v => {
          v = cloneDeep(v)
          v.fields = v.fields.map(field => {
            if (field.key.includes(displayAllPrefix)) {
              v.isRequestingDisplayAllPlot = true
              field.key = field.key.replace(displayAllPrefix, '')
            }
            return field
          })
          v.parent_analysis_axis = v.parent_analysis_axis?.replace(displayAllPrefix, '')
          return v
        })
      }
      const requests = filteredDisplayGraphs.map(graph => {
        return axios
          .get(`/api/companies/${ companyUuid }/analysis/reviews/graph_elements.json`, {
            headers: {
              'Content-Type': 'application/json'
            },
            params: { ...getters.plotsParams(graph), ...params, isRequestingDisplayAllPlot: graph.isRequestingDisplayAllPlot }
          })
      })
      return Promise.all(requests)
        .then(responses => {
          commit('readPlotsSuccess', responses)
        })
        .catch(err => {
          commit('readPlotsFailure')
          console.error('error', err)
        })
    },
    /**
     * CSV データ取得
     * @param { Function } commit
     * @param { Object } getters
     * @param { Object } payload
     * @return { Promise }
     */
    exportCsv({ commit, getters }, payload) {
      if (getters.aggs.length == 0) return false

      const { companyUuid, params } = payload
      commit('setIndividualAnalysisField', null)
      commit('exportCsv')
      return axios
        .post(`/api/companies/${ companyUuid }/export/analysis/reviews.json`,
          { ...getters.exportParams, ...params }
        )
        .then(() => {
          commit('exportCsvSuccess')
          return true
        })
        .catch(err => {
          commit('exportCsvFailure')
          console.error('error', err)
          return false
        })
    }
  }
}
